// @ai-generated 2026-07-03T19:50:00Z by dimitri.driediger@synqony.com using Claude Fable 5
import { useIsPortalSession } from '@Contexts/session';
import { type ApiRequisition, EnumContainerOrderAction, getShortUnitLabel } from '@Model';
import {
    type AsyncStateUpdate,
    Badge,
    Button,
    Checkbox,
    DateInputBase,
    FieldWithError,
    FlexRow,
    FloatingCard,
    Grid,
    GridColumns,
    iconSizeSm,
    isLinked,
    ListRow,
    type MetaData,
    RadioCardGroup,
    type RadioCardOption,
    renderDecimal,
    TextInput,
    useGuiBinder,
    useProp,
    useTranslate,
    useWizard,
    useWizardData,
    useWizardErrors,
    useWizardUpdate,
    ValueWithBadges,
    WizardStepTitle,
} from '@synqo/frontend-component-library';
import { formatMaterialName } from '@utils/requisitionDisplay';
import { IconPlus } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { type ContainerPosition, needsReview, originNote, positionTitle, reviewReason } from '../../containerOrder';
import { type RequisitionAction, carriesContainers } from '@utils/requisitionAction';
import { type RequisitionTerminMode, type RequisitionWizardState } from '../../RequisitionWizardState';
import { useServiceDetails } from '../Positions/useServiceDetails';
import { RequisitionActionChip } from '@Components/RequisitionActionChip';
import containerClasses from '../ContainerSteps.module.css';
import { SiteCard } from '../SiteCard';
import { useAvvCodes } from '../useAvvCodes';
import classes from '../WizardSteps.module.css';

export type SummaryStepErrorKey = 'termsAccepted';

const NO_POSITIONS: readonly ContainerPosition[] = [];

interface SummaryStepBodyProps {
    data: ApiRequisition;
    meta: MetaData<ApiRequisition> | undefined;
    canEdit: boolean;
    /** The action the order carries. Undefined while the wizard runs without container support. */
    action?: RequisitionAction;
    /** The containers of a container order; empty for a loose-material one. */
    containerPositions?: readonly ContainerPosition[];
    /**
     * Index of the step the "Ändern" link on the positions card leads back to. Defaults to the
     * positions step of the three-step wizard.
     */
    detailsStepIndex?: number;

    requestUpdateState: AsyncStateUpdate<ApiRequisition>;
}

export function SummaryStepBody({
    data,
    meta,
    canEdit,
    action,
    containerPositions = NO_POSITIONS,
    detailsStepIndex = 1,
    requestUpdateState,
}: SummaryStepBodyProps) {
    const i18n = useTranslate('');
    const errors = useWizardErrors<SummaryStepErrorKey>();
    const feState = useWizardData<RequisitionWizardState>();
    const updateFeState = useWizardUpdate<RequisitionWizardState>();
    const wizard = useWizard<RequisitionWizardState>();
    const avv = useAvvCodes();

    const positionCount = carriesContainers(action) ? containerPositions.length : data.positions.length;
    const serviceDetails = useServiceDetails();
    const isPortalSession = useIsPortalSession();

    const binder = useGuiBinder<ApiRequisition>({
        data,
        meta,
        canEdit,
        requestUpdateState,
    });
    const remarksField = useProp(binder, 'remarks');

    const { ensureCodes } = avv;
    const { ensureDetails } = serviceDetails;
    useEffect(() => {
        const serviceIds = data.positions
            .map((position) => (isLinked(position.service) ? position.service.foreign_key : undefined))
            .filter((id): id is string => !!id);

        ensureCodes(serviceIds);
        ensureDetails(serviceIds);
    }, [data.positions, ensureCodes, ensureDetails]);

    const saveDates = useCallback(
        (patch: Partial<Pick<ApiRequisition, 'date_from' | 'date_to'>>) => {
            void requestUpdateState(async (latest, saveData) => {
                const next = { ...latest, ...patch };
                // Keep the range consistent: "spätestens bis" may not lie before the desired date.
                if (next.date_from && next.date_to && next.date_to < next.date_from) {
                    next.date_to = next.date_from;
                }
                return await saveData(next);
            });
        },
        [requestUpdateState],
    );

    const terminOptions = useMemo<RadioCardOption<RequisitionTerminMode>[]>(
        () => [
            {
                value: 'sofort',
                title: i18n('Schnellstmöglich'),
                description: i18n('Nächster verfügbarer Termin'),
            },
            {
                value: 'wunsch',
                title: i18n('Wunschtermin'),
                description: i18n('Datum selbst festlegen'),
            },
        ],
        [i18n],
    );

    const setTerminMode = useCallback(
        (mode: RequisitionTerminMode) => {
            if (mode === 'sofort') {
                // Stash the dates so an accidental toggle back to "Wunschtermin" restores them;
                // the draft itself must not carry dates while "Schnellstmöglich" is selected.
                updateFeState({
                    terminMode: mode,
                    dateRangeExpanded: false,
                    stashedDateFrom: data.date_from,
                    stashedDateTo: data.date_to,
                });
                saveDates({ date_from: undefined, date_to: undefined });
                return true;
            }
            updateFeState({ terminMode: mode, dateRangeExpanded: !!feState.stashedDateTo });
            if (feState.stashedDateFrom || feState.stashedDateTo) {
                saveDates({ date_from: feState.stashedDateFrom, date_to: feState.stashedDateTo });
            }
            return true;
        },
        [updateFeState, saveDates, data.date_from, data.date_to, feState.stashedDateFrom, feState.stashedDateTo],
    );

    const removeDateTo = useCallback(() => {
        updateFeState({ dateRangeExpanded: false });
        saveDates({ date_to: undefined });
    }, [updateFeState, saveDates]);

    const { minFromDate, minToDate } = useMemo(() => {
        const now = new Date();
        const minFromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (!data.date_from) {
            return { minFromDate, minToDate: undefined };
        }
        const fromDate = dayjs(data.date_from).toDate();
        const minToDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
        return { minFromDate, minToDate };
    }, [data.date_from]);

    return (
        <>
            <WizardStepTitle
                title={i18n('Zusammenfassung & Abschluss')}
                subtitle={i18n('Prüfe deine Bestellung, ergänze ein Wunschdatum und eine Bemerkung.')}
            />

            <SiteCard site={data.site} onChange={() => wizard.goTo(0)} />

            <FloatingCard
                title={i18n('Positionen')}
                separateHeader={false}
                collapsible={false}
                className={containerClasses.cardHeaderStretch}
                additionalHeaderItems={
                    <div className={containerClasses.positionsHeader}>
                        {action && (
                            <span className={containerClasses.actionChip}>
                                <RequisitionActionChip requisition={data} />
                            </span>
                        )}
                        <Button
                            appearance="transparent"
                            size="xs"
                            className={containerClasses.changeLink}
                            onClick={() => wizard.goTo(detailsStepIndex)}
                        >
                            {i18n('Ändern')}
                        </Button>
                        <span className={containerClasses.positionCount}>
                            {`${positionCount} ${positionCount === 1 ? i18n('Position') : i18n('Positionen')}`}
                        </span>
                    </div>
                }
            >
                {containerPositions.map((position, index) => (
                    <ContainerSummaryRow
                        key={`${index}-${position.action}`}
                        position={position}
                        showsAction={containerPositions.length > 1}
                        i18n={i18n}
                    />
                ))}

                {/* A container order carries no material positions; any a loose-material order
                    left behind are on their way out and would show up here as a line of "0,000 t". */}
                {!carriesContainers(action) &&
                    data.positions.map((position) => {
                        const serviceId = isLinked(position.service) ? position.service.foreign_key : '';
                        const code = serviceId ? avv.codes.get(serviceId) : undefined;
                        const service = serviceId ? serviceDetails.detailsById.get(serviceId) : undefined;
                        return (
                            <ListRow
                                key={position.id}
                                title={
                                    service
                                        ? formatMaterialName(service, isPortalSession)
                                        : position.service.display_string
                                }
                                titleSuffix={code && ` · AVV ${code}`}
                                rightSection={
                                    <Badge variant="primary">
                                        {`${renderDecimal(position.quantity, 3)} ${getShortUnitLabel(position.unit)}`}
                                    </Badge>
                                }
                            />
                        );
                    })}
            </FloatingCard>

            <FloatingCard title="Wann sollen wir kommen?" separateHeader={false} collapsible={false}>
                <RadioCardGroup<RequisitionTerminMode>
                    name="termin_mode"
                    label={i18n('Wann sollen wir kommen?')}
                    options={terminOptions}
                    value={feState.terminMode}
                    onChange={setTerminMode}
                    locked={!canEdit}
                />
                {feState.terminMode === 'wunsch' && (
                    <Grid columns={2} gutter="md" align="end" fullWidth className={classes.terminDetails}>
                        <GridColumns spans={[1, 1]}>
                            <>
                                <FlexRow justify="space-between" align="center" className={classes.terminFieldHead}>
                                    <span className={classes.terminFieldLabel}>{i18n('Wunschdatum')}</span>
                                </FlexRow>
                                <DateInputBase
                                    type="day"
                                    name="date_from"
                                    ariaLabel={i18n('Wunschdatum')}
                                    value={data.date_from ?? ''}
                                    minDate={minFromDate}
                                    onChange={(value) => {
                                        saveDates({ date_from: value || undefined });
                                        return true;
                                    }}
                                />
                            </>
                            {feState.dateRangeExpanded ? (
                                <>
                                    <FlexRow justify="space-between" align="center" className={classes.terminFieldHead}>
                                        <span className={classes.terminFieldLabel}>{i18n('spätestens bis')}</span>
                                        <Button
                                            appearance="transparent"
                                            size="xs"
                                            onClick={removeDateTo}
                                            label={i18n('entfernen')}
                                        >
                                            {i18n('entfernen')}
                                        </Button>
                                    </FlexRow>
                                    {/* minDate stays at "today": earlier "bis" inputs are auto-corrected
                                                to the desired date by saveDates instead of being silently discarded. */}
                                    <DateInputBase
                                        type="day"
                                        name="date_to"
                                        ariaLabel={i18n('spätestens bis')}
                                        value={data.date_to ?? ''}
                                        minDate={minToDate}
                                        onChange={(value) => {
                                            saveDates({ date_to: value || undefined });
                                            return true;
                                        }}
                                    />
                                </>
                            ) : (
                                <Button
                                    appearance="transparent"
                                    size="sm"
                                    onClick={() => updateFeState({ dateRangeExpanded: true })}
                                    label={i18n('Zeitraum angeben')}
                                >
                                    <IconPlus size={iconSizeSm} aria-hidden="true" />
                                    {i18n('Zeitraum angeben')}
                                </Button>
                            )}
                        </GridColumns>
                    </Grid>
                )}

                <TextInput
                    {...remarksField}
                    inputSize={3}
                    label={`${i18n('Bemerkung')} (${i18n('optional')})`}
                    placeholder={i18n('z. B. Zufahrtshinweise, Ansprechpartner vor Ort, Stellplatz …')}
                />

                {/* Wizard errors refresh on the next "Weiter" only; hide as soon as the box is ticked. */}
                <FieldWithError error={feState.termsAccepted ? undefined : errors.termsAccepted}>
                    <Checkbox
                        name="termsAccepted"
                        value={feState.termsAccepted}
                        onChange={(value) => {
                            updateFeState({ termsAccepted: value });
                            return true;
                        }}
                        label={
                            <span>
                                {i18n('Ich akzeptiere die')}{' '}
                                {/* TODO(DEVCLD-2201): AGB-Dokument verlinken, sobald eine Ziel-URL existiert. */}
                                <a className={classes.agbLink} href="#agb" onClick={(event) => event.preventDefault()}>
                                    {i18n('AGB')}
                                </a>{' '}
                                {i18n('und die Datenschutzbestimmungen.')}
                            </span>
                        }
                        locked={!canEdit}
                    />
                </FieldWithError>
            </FloatingCard>
        </>
    );
}

export interface ContainerSummaryRowProps {
    position: ContainerPosition;
    /** Whether the line says what is done with its container, which it must where there are several. */
    showsAction: boolean;
    i18n: (key: string) => string;
}

/**
 * One container of the order, as the summary states it, with where it stands or is to stand.
 *
 * A position the dispatcher has to look at says so and why, so the customer learns before
 * ordering that this part of it is not confirmed yet.
 */
export function ContainerSummaryRow({ position, showsAction, i18n }: ContainerSummaryRowProps) {
    const needsCheck = needsReview(position);
    const origin = originNote(position);
    const location = position.locationHint && (
        <span className={containerClasses.locationNote}>{`${i18n('Stellplatz')}: ${position.locationHint}`}</span>
    );

    const title = (
        <span className={containerClasses.summaryTitle}>
            <strong>
                {showsAction ? `${actionLabel(position, i18n)}: ${positionTitle(position)}` : positionTitle(position)}
            </strong>
            {position.serviceLabel && (
                <span className={containerClasses.confirmedContent}>{` · ${position.serviceLabel}`}</span>
            )}
        </span>
    );

    if (!needsCheck) {
        return (
            <>
                <div className={containerClasses.summaryRow}>
                    {title}
                    {origin && <span className={containerClasses.originNote}>{i18n(origin)}</span>}
                </div>
                {location}
            </>
        );
    }

    return (
        <>
            <div className={containerClasses.reviewRow}>
                <ValueWithBadges value={title}>
                    <span className={containerClasses.reviewChip}>
                        <Badge>{i18n('Wird geprüft')}</Badge>
                    </span>
                </ValueWithBadges>
                <span className={containerClasses.reviewReason}>{reviewReason(position)}</span>
            </div>
            {location}
        </>
    );
}

function actionLabel(position: ContainerPosition, i18n: (key: string) => string): string {
    return position.action === EnumContainerOrderAction.Place ? i18n('Wir bringen') : i18n('Wir holen ab');
}
